import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, Camera, X, Loader2, LogOut, ChevronDown, Upload, CheckCircle2, Clock, ImagePlus, Hash } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function DocumentTerminal() {
    const { companySlug } = useParams();
    const [step, setStep] = useState('pin');
    const [pin, setPin] = useState('');
    const [storedPin, setStoredPin] = useState('');
    const [checking, setChecking] = useState(false);
    const [worker, setWorker] = useState(null);
    const [docTypes, setDocTypes] = useState([]);
    const [recentDocs, setRecentDocs] = useState([]);
    const [time, setTime] = useState(new Date());
    const inactivityRef = useRef(null);

    useEffect(() => {
        const t = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    const handleLogout = useCallback(() => {
        setStep('pin'); setPin(''); setStoredPin(''); setWorker(null);
        setDocTypes([]); setRecentDocs([]);
    }, []);

    const resetInactivity = useCallback(() => {
        if (inactivityRef.current) clearTimeout(inactivityRef.current);
        if (step === 'terminal') {
            inactivityRef.current = setTimeout(() => {
                handleLogout();
                toast.info('Inaktivite nedeniyle cikis yapildi');
            }, 5 * 60 * 1000);
        }
    }, [step, handleLogout]);

    useEffect(() => {
        if (step === 'terminal') {
            resetInactivity();
            const events = ['touchstart', 'mousedown', 'keydown'];
            events.forEach(e => document.addEventListener(e, resetInactivity));
            return () => {
                events.forEach(e => document.removeEventListener(e, resetInactivity));
                if (inactivityRef.current) clearTimeout(inactivityRef.current);
            };
        }
    }, [step, resetInactivity]);

    const handlePinDigit = (d) => { if (pin.length < 4) setPin(p => p + d); };
    const handlePinDelete = () => setPin(p => p.slice(0, -1));

    const handlePinSubmit = useCallback(async () => {
        if (pin.length !== 4) return;
        setChecking(true);
        try {
            const res = await axios.post(`${API}/api/documents/${companySlug}/verify`, { pin });
            setWorker(res.data);
            setStoredPin(pin);
            const [typesRes, recentRes] = await Promise.all([
                axios.get(`${API}/api/document-types/${companySlug}/public`),
                axios.get(`${API}/api/documents/${companySlug}/recent/${res.data.employee_id}`),
            ]);
            setDocTypes(typesRes.data);
            setRecentDocs(recentRes.data);
            setStep('terminal');
            setPin('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Gecersiz PIN');
            setPin('');
        } finally { setChecking(false); }
    }, [pin, companySlug]);

    useEffect(() => { if (pin.length === 4) handlePinSubmit(); }, [pin, handlePinSubmit]);

    return (
        <div className="min-h-screen bg-[#F5F5F7] select-none" data-testid="document-terminal" onContextMenu={e => e.preventDefault()}>
            <Toaster position="top-center" />
            {step === 'pin' && <PinScreen pin={pin} onDigit={handlePinDigit} onDelete={handlePinDelete} onSubmit={handlePinSubmit} checking={checking} time={time} />}
            {step === 'terminal' && worker && (
                <DocTerminalScreen
                    worker={worker} docTypes={docTypes} recentDocs={recentDocs}
                    setRecentDocs={setRecentDocs} companySlug={companySlug}
                    storedPin={storedPin} onLogout={handleLogout} time={time}
                />
            )}
        </div>
    );
}

function PinScreen({ pin, onDigit, onDelete, onSubmit, checking, time }) {
    return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-white">
            <div className="w-full max-w-xs">
                <div className="text-center mb-10">
                    <div className="inline-flex items-center gap-2 mb-5">
                        <FileText size={22} className="text-[#8B5CF6]" />
                        <span className="text-sm font-bold tracking-widest uppercase text-[#8B5CF6]">Dokuman Terminal</span>
                    </div>
                    <div className="text-5xl font-extralight text-[#111] tracking-wider mb-1" data-testid="doc-terminal-clock">
                        {time.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <div className="text-xs text-[#999] mt-1">
                        {time.toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </div>
                </div>
                <div className="flex justify-center gap-4 mb-3">
                    {[0,1,2,3].map(i => (
                        <div key={i} className={`w-3 h-3 rounded-full transition-all duration-200 ${i < pin.length ? 'bg-[#8B5CF6] scale-125' : 'bg-[#D1D5DB]'}`} />
                    ))}
                </div>
                <p className="text-center text-[#999] text-xs mb-6">PIN kodunuzu girin</p>
                <div className="grid grid-cols-3 gap-3 max-w-[240px] mx-auto">
                    {[1,2,3,4,5,6,7,8,9].map(d => (
                        <button key={d} onClick={() => onDigit(String(d))}
                            className="aspect-square rounded-2xl bg-[#F3F4F6] text-[#111] text-xl font-medium hover:bg-[#E5E7EB] active:bg-[#D1D5DB] active:scale-95 transition-all"
                            data-testid={`doc-pin-${d}`}>{d}</button>
                    ))}
                    <button onClick={onDelete} className="aspect-square rounded-2xl bg-[#F9FAFB] text-[#9CA3AF] text-xs hover:bg-[#F3F4F6] active:scale-95 transition-all" data-testid="doc-pin-delete">Sil</button>
                    <button onClick={() => onDigit('0')} className="aspect-square rounded-2xl bg-[#F3F4F6] text-[#111] text-xl font-medium hover:bg-[#E5E7EB] active:bg-[#D1D5DB] active:scale-95 transition-all" data-testid="doc-pin-0">0</button>
                    <button onClick={onSubmit} disabled={pin.length !== 4 || checking}
                        className="aspect-square rounded-2xl bg-[#8B5CF6] text-white text-xs font-semibold hover:bg-[#7C3AED] active:scale-95 transition-all disabled:opacity-30 flex items-center justify-center"
                        data-testid="doc-pin-submit">
                        {checking ? <Loader2 size={16} className="animate-spin" /> : 'Gir'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function DocTerminalScreen({ worker, docTypes, recentDocs, setRecentDocs, companySlug, storedPin, onLogout, time }) {
    const [selectedType, setSelectedType] = useState('');
    const [docNumber, setDocNumber] = useState('');
    const [notes, setNotes] = useState('');
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
    const fileRef = useRef(null);
    const cameraRef = useRef(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setTypeDropdownOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setFile(f);
        if (f.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (ev) => setPreview(ev.target.result);
            reader.readAsDataURL(f);
        } else {
            setPreview(null);
        }
    };

    const resetForm = () => {
        setSelectedType('');
        setDocNumber('');
        setNotes('');
        setFile(null);
        setPreview(null);
    };

    const handleUpload = async () => {
        if (!selectedType) return toast.error('Dokuman turu secin');
        if (!file) return toast.error('Dosya veya fotograf ekleyin');

        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('pin', storedPin);
            fd.append('document_type_id', selectedType);
            fd.append('document_number', docNumber);
            fd.append('notes', notes);
            fd.append('file', file);

            const res = await axios.post(`${API}/api/documents/${companySlug}/upload`, fd);
            setRecentDocs(prev => [res.data, ...prev].slice(0, 10));
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 2000);
            resetForm();
            toast.success('Dokuman yuklendi');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Yukleme hatasi');
        } finally { setUploading(false); }
    };

    const selectedTypeName = docTypes.find(t => t.id === selectedType)?.name || '';

    return (
        <div className="min-h-screen flex flex-col bg-[#F5F5F7]">
            {/* Header */}
            <header className="flex items-center justify-between px-3 py-2.5 bg-white border-b border-[#E5E7EB] sticky top-0 z-30">
                <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-[#8B5CF6]/10 flex items-center justify-center">
                        <span className="text-xs font-bold text-[#8B5CF6]">{worker.employee_name?.charAt(0)}</span>
                    </div>
                    <div>
                        <div className="text-sm font-semibold text-[#111]" data-testid="doc-worker-name">{worker.employee_name}</div>
                        <div className="text-[10px] text-[#9CA3AF] leading-tight">{worker.company_name}</div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-[10px] text-[#9CA3AF]">{time.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
                    <button onClick={onLogout} className="p-1.5 rounded-lg hover:bg-[#FEE2E2] text-[#9CA3AF] hover:text-[#EF4444] transition-colors" data-testid="doc-terminal-logout">
                        <LogOut size={16} />
                    </button>
                </div>
            </header>

            <main className="flex-1 px-3 py-3 max-w-lg mx-auto w-full space-y-3 pb-6">
                {/* Success overlay */}
                {showSuccess && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
                        <div className="bg-white rounded-2xl p-8 text-center shadow-xl">
                            <CheckCircle2 size={48} className="text-green-500 mx-auto mb-3" />
                            <p className="text-lg font-bold text-[#111]">Yuklendi!</p>
                        </div>
                    </div>
                )}

                {/* No document types warning */}
                {docTypes.length === 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center" data-testid="no-doc-types-warning">
                        <FileText size={24} className="text-amber-500 mx-auto mb-2" />
                        <p className="text-sm font-medium text-amber-800">Dokuman turu tanimlanmamis</p>
                        <p className="text-xs text-amber-600 mt-1">Yoneticinizden Ayarlar sayfasindan dokuman turleri eklemesini isteyin</p>
                    </div>
                )}

                {/* Upload Form */}
                {docTypes.length > 0 && (
                    <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 space-y-3" data-testid="doc-upload-form">
                        <h3 className="text-sm font-bold text-[#111] flex items-center gap-2">
                            <FileText size={16} className="text-[#8B5CF6]" />
                            Dokuman Yukle
                        </h3>

                        {/* Document Type Dropdown */}
                        <div className="relative" ref={dropdownRef}>
                            <label className="text-[10px] text-[#9CA3AF] font-semibold uppercase tracking-wider block mb-1">Dokuman Turu *</label>
                            <button
                                onClick={() => setTypeDropdownOpen(!typeDropdownOpen)}
                                className={`w-full h-11 px-3 flex items-center justify-between bg-[#F9FAFB] border rounded-xl text-sm transition-colors ${selectedType ? 'border-[#8B5CF6] text-[#111]' : 'border-[#E5E7EB] text-[#9CA3AF]'}`}
                                data-testid="doc-type-dropdown"
                            >
                                <span>{selectedTypeName || 'Tur secin...'}</span>
                                <ChevronDown size={14} className={`transition-transform ${typeDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {typeDropdownOpen && (
                                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E5E7EB] rounded-xl shadow-lg py-1 z-20 max-h-48 overflow-y-auto" data-testid="doc-type-menu">
                                    {docTypes.map(t => (
                                        <button
                                            key={t.id}
                                            onClick={() => { setSelectedType(t.id); setTypeDropdownOpen(false); }}
                                            className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${selectedType === t.id ? 'bg-[#8B5CF6]/10 text-[#8B5CF6] font-medium' : 'text-[#111] hover:bg-[#F9FAFB]'}`}
                                            data-testid={`doc-type-option-${t.id}`}
                                        >
                                            {t.name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Document Number */}
                        <div>
                            <label className="text-[10px] text-[#9CA3AF] font-semibold uppercase tracking-wider block mb-1">Dokuman Numarasi</label>
                            <div className="relative">
                                <Hash size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                                <input
                                    type="text"
                                    value={docNumber}
                                    onChange={e => setDocNumber(e.target.value)}
                                    placeholder="Numara girin..."
                                    className="w-full h-11 pl-9 pr-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm text-[#111] placeholder:text-[#C5C5C7] outline-none focus:border-[#8B5CF6] transition-colors"
                                    data-testid="doc-number-input"
                                />
                            </div>
                        </div>

                        {/* Notes */}
                        <div>
                            <label className="text-[10px] text-[#9CA3AF] font-semibold uppercase tracking-wider block mb-1">Not (Opsiyonel)</label>
                            <input
                                type="text"
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                                placeholder="Aciklama..."
                                className="w-full h-11 px-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl text-sm text-[#111] placeholder:text-[#C5C5C7] outline-none focus:border-[#8B5CF6] transition-colors"
                                data-testid="doc-notes-input"
                            />
                        </div>

                        {/* Photo / File */}
                        <div>
                            <label className="text-[10px] text-[#9CA3AF] font-semibold uppercase tracking-wider block mb-1">Fotograf / Dosya *</label>
                            {preview ? (
                                <div className="relative rounded-xl overflow-hidden border border-[#E5E7EB]">
                                    <img src={preview} alt="Preview" className="w-full h-48 object-contain bg-[#F9FAFB]" data-testid="doc-preview-image" />
                                    <button
                                        onClick={() => { setFile(null); setPreview(null); }}
                                        className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 flex items-center justify-center text-white"
                                        data-testid="doc-remove-file"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            ) : file ? (
                                <div className="flex items-center gap-2 px-3 py-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl">
                                    <FileText size={16} className="text-[#8B5CF6] shrink-0" />
                                    <span className="text-sm text-[#111] truncate flex-1">{file.name}</span>
                                    <button onClick={() => { setFile(null); setPreview(null); }} className="text-[#9CA3AF] hover:text-red-500">
                                        <X size={14} />
                                    </button>
                                </div>
                            ) : (
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => cameraRef.current?.click()}
                                        className="flex-1 h-24 flex flex-col items-center justify-center gap-1.5 bg-[#F9FAFB] border-2 border-dashed border-[#D1D5DB] rounded-xl hover:border-[#8B5CF6] transition-colors"
                                        data-testid="doc-camera-btn"
                                    >
                                        <Camera size={24} className="text-[#9CA3AF]" />
                                        <span className="text-xs text-[#9CA3AF]">Fotograf Cek</span>
                                    </button>
                                    <button
                                        onClick={() => fileRef.current?.click()}
                                        className="flex-1 h-24 flex flex-col items-center justify-center gap-1.5 bg-[#F9FAFB] border-2 border-dashed border-[#D1D5DB] rounded-xl hover:border-[#8B5CF6] transition-colors"
                                        data-testid="doc-file-btn"
                                    >
                                        <ImagePlus size={24} className="text-[#9CA3AF]" />
                                        <span className="text-xs text-[#9CA3AF]">Dosya Sec</span>
                                    </button>
                                </div>
                            )}
                            <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={handleFile} className="hidden" />
                            <input ref={fileRef} type="file" accept="image/*,.pdf,.doc,.docx" onChange={handleFile} className="hidden" />
                        </div>

                        {/* Upload Button */}
                        <button
                            onClick={handleUpload}
                            disabled={uploading || !selectedType || !file}
                            className="w-full h-12 bg-[#8B5CF6] text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.98] transition-all"
                            data-testid="doc-upload-btn"
                        >
                            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                            {uploading ? 'Yukleniyor...' : 'Dokumani Yukle'}
                        </button>
                    </div>
                )}

                {/* Recent Documents */}
                {recentDocs.length > 0 && (
                    <div data-testid="recent-documents">
                        <h3 className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold mb-1.5 px-1">Son Yuklemeler</h3>
                        <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden divide-y divide-[#F3F4F6]">
                            {recentDocs.slice(0, 8).map((doc, i) => (
                                <div key={doc.id} className={`flex items-center gap-2.5 px-3 py-2.5 ${i === 0 ? 'bg-[#F5F3FF]' : ''}`} data-testid={`recent-doc-${i}`}>
                                    <div className="w-8 h-8 rounded-lg bg-[#8B5CF6]/10 flex items-center justify-center shrink-0">
                                        <FileText size={14} className="text-[#8B5CF6]" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-xs font-medium text-[#111] truncate">
                                            {doc.document_type_name}
                                            {doc.document_number && <span className="text-[#6B7280] font-normal"> #{doc.document_number}</span>}
                                        </div>
                                        <div className="text-[10px] text-[#C5C5C7]">
                                            {doc.created_at ? new Date(doc.created_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                                        </div>
                                    </div>
                                    {doc.file_url && (
                                        <a href={doc.file_url} target="_blank" rel="noopener noreferrer"
                                            className="text-[10px] text-[#8B5CF6] font-medium px-2 py-1 rounded-lg hover:bg-[#8B5CF6]/10 transition-colors">
                                            Gor
                                        </a>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
